import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import { useSeo } from "@/hooks/useSeo";
import {
  categories,
  getCategoryBySlug,
  getMoviesByCategory,
  getYears,
  paginate,
  sortMovies,
} from "@/utils/movie";
import NotFound from "@/pages/NotFound";

const SORTS = [
  { value: "latest", label: "最新上映" },
  { value: "hot", label: "最多观看" },
  { value: "rating", label: "评分最高" },
];

export default function CategoryPage() {
  const { type } = useParams();
  const category = getCategoryBySlug(type);
  const baseMovies = useMemo(() => getMoviesByCategory(type), [type]);
  const years = getYears();

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [year, setYear] = useState<string>("all");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setSelectedTags([]);
    setYear("all");
    setSort("latest");
    setPage(1);
  }, [type]);

  useSeo({
    title: category ? `${category.name}电影 - 免费在线电影` : "分类电影 - 免费在线电影",
    description: category
      ? `免费在线电影为你整理最新最全的${category.name}电影，共收录 ${baseMovies.length} 部${category.name}影片，${category.desc}`
      : "按分类浏览高清电影资源。",
    keywords: category
      ? `${category.name}电影,${category.name}片,免费在线电影,高清${category.name}电影`
      : "电影分类,免费在线电影",
  });

  const filtered = useMemo(() => {
    let list = baseMovies;
    if (selectedTags.length > 0) {
      list = list.filter((m) => selectedTags.every((t) => m.genreSlugs.includes(t)));
    }
    if (year !== "all") {
      list = list.filter((m) => String(m.year) === year);
    }
    return sortMovies(list, sort);
  }, [baseMovies, selectedTags, year, sort]);

  const { items, totalPages, currentPage, total } = paginate(filtered, page, 12);

  if (!category) return <NotFound />;

  const toggleTag = (slug: string) => {
    setPage(1);
    setSelectedTags((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const resetFilters = () => {
    setSelectedTags([]);
    setYear("all");
    setSort("latest");
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-primary-950">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-100/80" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>分类</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">{category.name}</span>
          </nav>
          <div className="mt-4 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-background-50">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">{category.name}电影</h1>
              <p className="mt-1 text-sm text-background-100/80">共 {baseMovies.length} 部影片 · {category.desc}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-8 md:px-6">
        {/* Filter bar */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between md:hidden">
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
              <i className="ri-filter-3-line text-accent-500"></i>
              筛选条件
            </span>
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex items-center gap-1 text-sm text-primary-600"
            >
              {filtersOpen ? "收起" : "展开"}
              <i className={`ri-arrow-down-s-line transition-transform ${filtersOpen ? "rotate-180" : ""}`}></i>
            </button>
          </div>

          <div className={`${filtersOpen ? "block" : "hidden"} mt-4 md:block md:mt-0`}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-sm font-medium text-foreground-700">类型：</span>
                <button
                  type="button"
                  onClick={() => { setSelectedTags([]); setPage(1); }}
                  className={`rounded-full px-3 py-1 text-xs transition-colors ${selectedTags.length === 0 ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"}`}
                >
                  全部
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => toggleTag(cat.slug)}
                    className={`rounded-full px-3 py-1 text-xs transition-colors ${selectedTags.includes(cat.slug) ? "bg-accent-500 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"}`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-foreground-700">
                  年份：
                  <select
                    value={year}
                    onChange={(e) => { setYear(e.target.value); setPage(1); }}
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none focus:border-accent-400"
                  >
                    <option value="all">全部年份</option>
                    {years.map((y) => (
                      <option key={y} value={String(y)}>{y} 年</option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground-700">
                  排序：
                  <select
                    value={sort}
                    onChange={(e) => { setSort(e.target.value); setPage(1); }}
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none focus:border-accent-400"
                  >
                    {SORTS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex items-center gap-1 rounded-md border border-background-200 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className="ri-refresh-line"></i>
                  重置
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-6 text-sm text-foreground-500">
          共找到 <span className="font-semibold text-accent-600">{total}</span> 部符合条件的电影
        </p>

        {items.length > 0 ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {items.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16">
            <i className="ri-movie-2-line text-5xl text-foreground-300"></i>
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">暂无符合条件的电影</p>
            <p className="mt-1 text-sm text-foreground-500">换个筛选条件试试吧</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 rounded-md bg-accent-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}